import {Request} from "../core/request";
import {AuthHeader} from "../core/token";
import {CourseSubject} from "../course/location";
import {HomeworkPapers} from "../homeworkBank/types";
import { GradeBandInfo } from '../../utils/gradeBandUtils';
import type {StudentClass, StudentClassInfo} from "./types";

// 获取我的班级列表
export async function getMyClasses(): Promise<{ data: StudentClass[] }> {
    const resp = await Request.get(`/student/class/`, {
        headers: AuthHeader(),
    });
    return resp.data;
}

// 获取班级信息
export async function getClassInfo(classID: number): Promise<{ data: StudentClassInfo }> {
    const resp = await Request.get(`/student/class/${classID}`, {
        headers: AuthHeader(),
    });
    return resp.data;
}


export interface QuestionMapDetail {
    correct_number: number;     // 正确题数
    max_number: number;         // 总题数（原注释“错误”可能有误，应为总数）
    correct_index: number[];    // 正确题目下标
    max_index: number;          // 最大下标（通常是题目总数 - 1）
}

export interface AnswerDetail {
    score: number;                          // 实际得分（原 total_score）
    max_score: number;                      // 满分
    QuestionMap?: Record<string, QuestionMapDetail | null>; // API 返回的字段名（大写Q）
    question_map?: Record<string, QuestionMapDetail | null>; // 兼容小写字段名
    use_time?: number; // Go 的 time.Duration 在 JSON 中序列化为纳秒数（可选）
}


export type getHomeworkRequest = {
    classID: number;
    subject_id: number;

    page: number;
    size: number;
    exam_mode?: boolean | string;
}

export type homework = {
    ID: number;
    CreatedAt: string,
    UpdatedAt: string,

    name: string,
    description: string,
    use_time: number,
    questions?: HomeworkPapers,
    score?: number;              // 实际得分（已完成答题的作业才有）
    max_score?: number;           // 满分（已完成答题的作业才有）
    answer_detail?: AnswerDetail  // 可选，保留兼容性（包含 QuestionMap 和 use_time）

    subject_id: number // 学科ID
    subject: string    // 学科 必选

    temp_id: number,
    enable: boolean,
    class_id: number
    exam_mode?: boolean
    
    has_answer?: boolean    // 是否已作答
    is_finished?: boolean  // 是否已完成
    can_continue?: boolean // 是否可以继续答题
    status?: string        // start | wait_raed | finish（兼容）
    answer_status?: string // 答案状态：start | wait_raed | finish
    grade_band?: GradeBandInfo
}


export type getHomeworksResponse = {
    data: homework[];
    total: number;
    page: number,
    size: number;
}

export type answer_message = {
    id: string;
    answer: string[];
    start_time: string;
    end_time: string;
    result: number;
    correctness?: string;   // correct | incorrect | half_correct | custom
    grade_desc?: string;   // 批改备注
    answer_extra?: Record<string, unknown>;
    grade_extra?: Record<string, unknown>;
}


export type Answer = {
    status: string;
    start_time: string
    end_time: string

    result: number
    count_result: number
    wait_read: boolean
    answer_results: answer_message[]
    answer_detail: AnswerDetail
}


export async function getHomeworks(req: getHomeworkRequest): Promise<getHomeworksResponse> {
    const resp = await Request.get(`/student/homeworks/me/`, {
        headers: AuthHeader(),
        params: req,
    });
    return resp.data;
}

export async function getNotAnswersHomework(classID?: number): Promise<getHomeworksResponse> {
    const resp = await Request.get(`/student/homeworks/not_answers`, {
        headers: AuthHeader(),
        params: classID ? { classID } : {},
    });
    return resp.data;
}

export type getHomeworkDetailResponse = homework & {
    has_answer: boolean,
    is_finished: boolean,
    can_continue: boolean,
    answer: Answer,
    grade_band?: import('../../utils/gradeBandUtils').GradeBandInfo,
    exam_duration_seconds?: number,
    exam_deadline?: string,
    remaining_seconds?: number,
    exam_started?: boolean,
    exam_expired?: boolean,
    answer_use_time_seconds?: number,
}

export type StartExamHomeworkResponse = {
    start_time: string;
    exam_deadline: string;
    exam_duration_seconds: number;
    remaining_seconds: number;
}

export async function startExamHomework(id: number): Promise<StartExamHomeworkResponse> {
    const resp = await Request.post(`/student/homeworks/me/${id}/start_exam`, {}, {
        headers: AuthHeader(),
    });
    return resp.data;
}

export async function getHomeworkDetail(id: number): Promise<getHomeworkDetailResponse> {
    const resp = await Request.get(`/student/homeworks/me/${id}`, {
        headers: AuthHeader(),
    });
    return resp.data;
}


export type submitHomeworkRequest = {
    is_cache: boolean,
    answer_results: answer_message[],
    homework_start_time?: string  // 用户进入答题页时的 ISO 时间，用于计算真实答题用时
}

export async function submitHomework(id: number, req: submitHomeworkRequest): Promise<void> {
    const resp = await Request.put(`/student/homeworks/me/${id}`, req, {
        headers: AuthHeader(),
    })
    return resp.data;
}

export async function getSubjects() :Promise<CourseSubject[]>{
    const resp = await Request.get(`/student/homeworks/subjects`, {
        headers: AuthHeader(),
    })
    return resp.data
}